"use client";
import React from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Intro from "@/ui/Intro";

const extractText = (richText) => {
    if (!richText) return "";
    if (typeof richText === "string") return richText.trim();
    if (Array.isArray(richText)) {
        return richText.map(block => extractText(block)).join(" ").trim();
    }
    if (richText.children && Array.isArray(richText.children)) {
        return richText.children
            .map(child => {
                if (child.type === "text" && child.text) return child.text;
                return extractText(child);
            })
            .join("")
            .trim();
    }
    return "";
};

const ResearchPublications = ({ research = [] }) => {
    let researchArray = [];
    if (Array.isArray(research)) {
        researchArray = research;
    } else if (research?.researcher) {
        researchArray = Array.isArray(research.researcher) ? research.researcher : [research.researcher];
    }
    if (!researchArray?.length) return null;
    const normalizedresearch = researchArray.map((rData, idx) => {
        const r = rData.attributes || rData;
        return {
            id: rData.id ?? idx,
            name: extractText(r.Name || r.Title),
            description: extractText(r.description),
            image: r.image?.data?.[0]?.attributes?.url ||
                r.image?.url ||
                r.image ||
                ""
        };
    }).filter(r => r.name);

    if (!normalizedresearch.length) return null;

    return (
        <section className="relative w-full">
            {/* Header row — inside page container */}
            <div className="page-container py-[12px] md:py-[14px] mx-auto">
                <div className="flex items-center justify-between gap-4">
                    <Intro
                        title="Research & Publications"
                        description="Explore impactful research, scholarly contributions, and evidence-based insights authored by our faculty, reflecting MSU's commitment to innovation, knowledge creation, and academic leadership."
                    />
                    <div className="hidden md:flex items-center gap-3 shrink-0">
                        <button
                            className="research-prev inline-flex h-9 w-9 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-700 hover:border-gray-400 hover:bg-gray-50 transition-colors"
                            type="button"
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </button>
                        <button
                            className="research-next inline-flex h-9 w-9 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-700 hover:border-gray-400 hover:bg-gray-50 transition-colors"
                            type="button"
                        >
                            <ChevronRight className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Full-width blue swiper — outside page-container intentionally */}
            <div className="w-full bg-[#0475B3] px-4 md:px-8 lg:px-10 py-8 md:py-10 overflow-hidden">
                {/* Mobile nav buttons */}
                <div className="flex justify-end gap-3 mb-4 md:hidden">
                    <button
                        className="research-prev inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/40 bg-white/10 text-white hover:bg-white/20 transition-colors"
                        type="button"
                    >
                        <ChevronLeft className="h-4 w-4" />
                    </button>
                    <button
                        className="research-next inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/40 bg-white/10 text-white hover:bg-white/20 transition-colors"
                        type="button"
                    >
                        <ChevronRight className="h-4 w-4" />
                    </button>
                </div>

                <Swiper
                    modules={[Navigation]}
                    navigation={{
                        prevEl: ".research-prev",
                        nextEl: ".research-next",
                    }}
                    spaceBetween={20}
                    slidesPerView={1.2}
                    breakpoints={{
                        480: { slidesPerView: 1.8, spaceBetween: 20 },
                        640: { slidesPerView: 2.3, spaceBetween: 20 },
                        768: { slidesPerView: 3,   spaceBetween: 24 },
                        1024:{ slidesPerView: 4,   spaceBetween: 24 },
                        1280:{ slidesPerView: 5,   spaceBetween: 24 },
                    }}
                >
                    {normalizedresearch.map((item) => (
                        <SwiperSlide key={item.id}>
                            <article className="w-full">
                                <div className="aspect-[3/4] w-full overflow-hidden bg-white">
                                    {item.image ? (
                                        <img
                                            src={item.image}
                                            alt={item.name}
                                            className="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
                                        />
                                    ) : (
                                        <div className="h-full w-full flex items-center justify-center text-xs text-gray-400">
                                            No Image
                                        </div>
                                    )}
                                </div>
                                <div className="mt-3 text-white text-left">
                                    <h3
                                        className="text-xs font-semibold mb-1 line-clamp-2"
                                        style={{ fontFamily: "saans-bold" }}
                                    >
                                        {item.name}
                                    </h3>
                                    {item.description && (
                                        <p className="text-[11px] leading-snug text-white/80 line-clamp-3">
                                            {item.description}
                                        </p>
                                    )}
                                </div>
                            </article>
                        </SwiperSlide>
                    ))}
                </Swiper>
            </div>
        </section>
    );
};

export default ResearchPublications;
